import { ApiRecord, rec } from '../../../models/api-response.model';

/** Generates a workspace customer id for a new co-owner / co-applicant party. */
export function createCoOwnerCustomerId(): string {
  return crypto.randomUUID();
}

export function isCoOwnerFlowType(
  flowType: 'primary' | 'coOwnerEarner' | 'coOwnerNonEarner',
): boolean {
  return flowType !== 'primary';
}

export const DEFAULT_OWN_CONTRIBUTION = '5,00,000';
export const DEFAULT_LOAN_AMOUNT = '25,00,000';
export const DEFAULT_TENURE_MONTHS = 60;

export const CO_APPLICANT_IS_EARNER_KYC_KEY = 'coApplicantIsEarner';

export function maskPanForDisplay(value: string | null | undefined): string {
  const normalized = String(value ?? '').trim().toUpperCase();
  if (!normalized || normalized === '—') {
    return '';
  }
  if (/^[*X]{4}/.test(normalized)) {
    return normalized;
  }
  if (normalized.length < 4) {
    return '****';
  }
  return `****${normalized.slice(-4)}`;
}

function readExplicitCoApplicantIsEarner(
  record: ApiRecord | null | undefined,
): boolean | null {
  if (!record) {
    return null;
  }

  const direct = record[CO_APPLICANT_IS_EARNER_KYC_KEY];
  if (direct === true || direct === false) {
    return direct;
  }

  const kyc = rec(record['kyc']);
  const explicit =
    kyc[CO_APPLICANT_IS_EARNER_KYC_KEY] ?? kyc['co_applicant_is_earner'];
  if (explicit === true || explicit === false) {
    return explicit;
  }

  return null;
}

function hasFinancialEarnerSignal(fin: ApiRecord): boolean {
  const financeType = String(fin['financeType'] ?? fin['finance_type'] ?? '')
    .trim()
    .toLowerCase();
  if (financeType === 'loan' || financeType === 'home_loan') {
    return true;
  }

  const keys = [
    ['salaryCreditBank', 'salary_credit_bank'],
    ['creditMode', 'credit_mode'],
    ['netMonthlySalary', 'net_monthly_salary'],
  ] as const;

  return keys.some(([camelKey, snakeKey]) => {
    const value = fin[camelKey] ?? fin[snakeKey];
    if (value == null) {
      return false;
    }
    if (typeof value === 'number') {
      return value > 0;
    }
    return String(value).trim().length > 0;
  });
}

/** Infers whether a saved co-owner/co-applicant is an earner from API data. */
export function inferCoOwnerIsEarnerFromRecord(
  record: ApiRecord | null | undefined,
): boolean {
  const explicit = readExplicitCoApplicantIsEarner(record);
  if (explicit !== null) {
    return explicit;
  }

  if (!record) {
    return true;
  }

  const financial = rec(record['financial']);
  if (!Object.keys(financial).length) {
    return false;
  }

  return hasFinancialEarnerSignal(financial);
}

/** Resolves earner status for edit flows (explicit flag first). */
export function resolveCoApplicantIsEarnerForEdit(
  record: ApiRecord | null | undefined,
): boolean {
  return (
    readExplicitCoApplicantIsEarner(record) ?? inferCoOwnerIsEarnerFromRecord(record)
  );
}

export function createEmptyPropertyOwnerFinancialState(): {
  fin: {
    salaryCreditBank: string;
    creditMode: string;
    netMonthlySalary: string;
    otherMonthlyIncome: string;
    currentEmiTotal: string;
    creditCardOutstanding: string;
  };
  financeType: 'self' | 'loan';
  ownContribution: string;
  loanAmount: string;
  tenureMonths: number;
} {
  return {
    fin: {
      salaryCreditBank: '',
      creditMode: '',
      netMonthlySalary: '0',
      otherMonthlyIncome: '0',
      currentEmiTotal: '0',
      creditCardOutstanding: '0',
    },
    financeType: 'self',
    ownContribution: DEFAULT_OWN_CONTRIBUTION,
    loanAmount: DEFAULT_LOAN_AMOUNT,
    tenureMonths: DEFAULT_TENURE_MONTHS,
  };
}
